import React from 'react';
import { CreditCard, FileText, Calculator, TrendingUp, Users, Clock, CheckCircle, ArrowRight, Building2, MessageCircle, Star, BarChart3 } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const CorrespondentePage = () => {
  const features = [
    {
      icon: <CreditCard className="h-8 w-8 text-blue-600" />,
      title: "Análise de Crédito Instantânea",
      description: "Nossa IA analisa a capacidade de pagamento em segundos, considerando renda, score e histórico financeiro.",
      benefits: ["Aprovação 45% mais rápida", "Redução de inadimplência", "Análise de 50+ variáveis"]
    },
    {
      icon: <FileText className="h-8 w-8 text-blue-600" />,
      title: "Documentação Automatizada",
      description: "Gere automaticamente todos os documentos necessários: propostas, contratos e relatórios.",
      benefits: ["Templates personalizados", "Assinatura digital", "Conformidade bancária"]
    },
    {
      icon: <Calculator className="h-8 w-8 text-blue-600" />,
      title: "Simulador Avançado",
      description: "Calcule diferentes cenários de financiamento com taxas atualizadas em tempo real.",
      benefits: ["Múltiplos bancos", "Taxas em tempo real", "Comparativo automático"]
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-blue-600" />,
      title: "Dashboard de Performance",
      description: "Acompanhe suas vendas, comissões e performance com relatórios detalhados.",
      benefits: ["Métricas em tempo real", "Relatórios personalizados", "Metas e objetivos"]
    }
  ];

  const testimonials = [
    {
      name: "Carlos Silva",
      role: "Correspondente Bancário - SP",
      content: "Aumentei minhas vendas em 180% em 6 meses. O ImóvelIA me deu a vantagem competitiva que eu precisava.",
      rating: 5,
      image: "https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg"
    },
    {
      name: "Ana Costa",
      role: "Correspondente Bancário - RJ",
      content: "A análise automatizada me economiza 4 horas por dia. Agora consigo atender muito mais clientes.",
      rating: 5,
      image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-blue-100 font-medium mb-6">
              <CreditCard className="h-5 w-5" />
              <span>Especializado para Correspondentes Bancários</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Multiplique suas Vendas de Financiamento com IA
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">
              Automatize análises de crédito, gere documentos instantaneamente e aumente sua taxa de aprovação em até 45%
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/5516999631188?text=Olá! Sou correspondente bancário e quero saber mais sobre o ImóvelIA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-lg font-medium transition-colors text-lg"
              >
                <MessageCircle className="h-5 w-5" />
                Falar com Especialista
              </a>
              <a
                href="https://pay.kiwify.com.br/cSvs3ux"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-8 py-4 rounded-lg font-medium transition-colors text-lg"
              >
                <CreditCard className="h-5 w-5" />
                Começar Teste Grátis
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Ferramentas que Transformam sua Operação
            </h2>
            <p className="text-xl text-slate-600">
              Tudo que você precisa para ser o correspondente mais eficiente da sua região
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Resultados Comprovados
            </h2>
            <p className="text-xl text-slate-600">
              Veja o impacto real do ImóvelIA na carreira dos nossos correspondentes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">80%</div>
              <div className="text-slate-600">Redução no tempo de análise</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">45%</div>
              <div className="text-slate-600">Aumento na taxa de aprovação</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">R$ 28k</div>
              <div className="text-slate-600">Comissão média mensal</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">24h</div>
              <div className="text-slate-600">Suporte disponível</div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold">{testimonial.name}</h4>
                    <p className="text-sm text-slate-600">{testimonial.role}</p>
                  </div>
                  <div className="ml-auto flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                </div>
                <p className="text-slate-700 italic">"{testimonial.content}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para Revolucionar sua Carreira?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Junte-se aos correspondentes que já descobriram o poder da automação para multiplicar suas vendas
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/5516999631188?text=Olá! Quero uma demonstração personalizada do ImóvelIA para correspondentes"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-lg font-medium transition-colors"
            >
              <Users className="h-5 w-5" />
              Agendar Demonstração
              <ArrowRight className="h-5 w-5" />
            </a>
            <a
              href="https://pay.kiwify.com.br/cSvs3ux"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-8 py-4 rounded-lg font-medium transition-colors"
            >
              <CreditCard className="h-5 w-5" />
              Começar Agora - R$ 79,90/mês
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CorrespondentePage;